import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { ORDER_STATUS_DISPLAY, UserRole } from '../../types';
import { useGetActiveDealersByManufacturerIdQuery } from '../dealer/dealer-api-slice';
import { useGetOrdersDueReportQuery } from '../order/orders-api-slice';
import { selectCurrentUser } from '../user';
import {
  ManufacturerFilter,
  ReportLayout,
  ReportTablePagination,
  SortableHeadCell,
  useSortableTable,
} from './components';
import { formatReportCurrency, formatReportDate } from './report-utils';
import type { OrderDueSummary } from '../../types';

const ORDERS_DUE_SORT_ACCESSORS: Record<string, (order: OrderDueSummary) => string | number> = {
  orderNumber: o => o.orderNumber,
  dealerName: o => o.dealerName ?? '',
  customer: o => o.customer,
  dueDate: o => o.dueDate,
  status: o => ORDER_STATUS_DISPLAY[o.status] ?? `Status ${o.status}`,
  total: o => o.total,
};

type QuickSelect = 'custom' | 'this-week' | 'next-week' | 'this-month' | 'next-month';

function getQuickSelectDates(option: QuickSelect): { start: string; end: string } | null {
  if (option === 'custom') return null;

  const now = DateTime.local();

  switch (option) {
    case 'this-week': {
      const monday = now.startOf('week');
      const sunday = monday.plus({ days: 6 });
      return { start: monday.toISODate()!, end: sunday.toISODate()! };
    }
    case 'next-week': {
      const nextMonday = now.startOf('week').plus({ weeks: 1 });
      const nextSunday = nextMonday.plus({ days: 6 });
      return { start: nextMonday.toISODate()!, end: nextSunday.toISODate()! };
    }
    case 'this-month': {
      const firstDay = now.startOf('month');
      const lastDay = now.endOf('month');
      return { start: firstDay.toISODate()!, end: lastDay.toISODate()! };
    }
    case 'next-month': {
      const nextMonth = now.plus({ months: 1 });
      const firstDay = nextMonth.startOf('month');
      const lastDay = nextMonth.endOf('month');
      return { start: firstDay.toISODate()!, end: lastDay.toISODate()! };
    }
  }
}

export default function OrdersDueReport() {
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);

  const isAdmin = currentUser?.role === UserRole.ADMINISTRATOR;
  const isManufacturer =
    isAdmin || currentUser?.role === UserRole.SUPERVISOR || currentUser?.role === UserRole.CSR;

  const defaultDates = getQuickSelectDates('this-week')!;
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  const [quickSelect, setQuickSelect] = useState<QuickSelect>('this-week');
  const [selectedDealerId, setSelectedDealerId] = useState<number | undefined>(undefined);
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<number | undefined>(
    undefined
  );

  const [submittedStartDate, setSubmittedStartDate] = useState(defaultDates.start);
  const [submittedEndDate, setSubmittedEndDate] = useState(defaultDates.end);
  const [submittedDealerId, setSubmittedDealerId] = useState<number | undefined>(undefined);
  const [submittedManufacturerId, setSubmittedManufacturerId] = useState<number | undefined>(
    undefined
  );

  const [dateError, setDateError] = useState<string | null>(null);

  // Resolve the effective manufacturer ID: admin uses selected, others use their own
  const resolvedManufacturerId =
    isAdmin && selectedManufacturerId ? selectedManufacturerId : currentUser?.manufacturerId;

  // Dealer dropdown: fetch dealers for the resolved manufacturer
  const { data: dealers = [] } = useGetActiveDealersByManufacturerIdQuery(resolvedManufacturerId!, {
    skip: !isManufacturer || !resolvedManufacturerId,
  });

  // Resolve the manufacturer ID for the submitted query
  const effectiveManufacturerId =
    isAdmin && submittedManufacturerId ? submittedManufacturerId : currentUser?.manufacturerId;

  const {
    data: reportData,
    isLoading,
    error,
  } = useGetOrdersDueReportQuery(
    {
      startDate: submittedStartDate,
      endDate: submittedEndDate,
      dealerId: submittedDealerId,
      manufacturerId: effectiveManufacturerId,
    },
    { skip: !submittedStartDate || !submittedEndDate || !effectiveManufacturerId }
  );

  // Reset dealer selection when manufacturer changes
  const handleManufacturerChange = useCallback((mfgId: number | undefined) => {
    setSelectedManufacturerId(mfgId);
    setSelectedDealerId(undefined);
  }, []);

  const handleQuickSelectChange = useCallback((option: QuickSelect) => {
    setQuickSelect(option);
    const dates = getQuickSelectDates(option);
    if (dates) {
      setStartDate(dates.start);
      setEndDate(dates.end);
      setDateError(null);
    }
  }, []);

  const handleUpdateReport = useCallback(() => {
    if (!startDate) {
      setDateError('Start Date is required');
      return;
    }
    if (!endDate) {
      setDateError('End Date is required');
      return;
    }
    if (startDate > endDate) {
      setDateError('Start Date must be on or before End Date');
      return;
    }
    setDateError(null);
    setSubmittedStartDate(startDate);
    setSubmittedEndDate(endDate);
    setSubmittedDealerId(selectedDealerId);
    setSubmittedManufacturerId(selectedManufacturerId);
  }, [startDate, endDate, selectedDealerId, selectedManufacturerId]);

  const handleStartDateChange = useCallback(
    (value: string) => {
      setStartDate(value);
      setQuickSelect('custom');
      if (value && endDate && value > endDate) {
        setDateError('Start Date must be on or before End Date');
      } else {
        setDateError(null);
      }
    },
    [endDate]
  );

  const handleEndDateChange = useCallback(
    (value: string) => {
      setEndDate(value);
      setQuickSelect('custom');
      if (startDate && value && startDate > value) {
        setDateError('End Date must be on or after Start Date');
      } else {
        setDateError(null);
      }
    },
    [startDate]
  );

  const titleDateRange = useMemo(() => {
    if (!submittedStartDate || !submittedEndDate) return '';
    return `${formatReportDate(submittedStartDate)} - ${formatReportDate(submittedEndDate)}`;
  }, [submittedStartDate, submittedEndDate]);

  const needsManufacturer = isAdmin && !effectiveManufacturerId;

  const { pageRows, page, rowsPerPage, getSortProps, handleChangePage, handleChangeRowsPerPage } =
    useSortableTable<OrderDueSummary>({
      data: reportData?.orders ?? [],
      accessors: ORDERS_DUE_SORT_ACCESSORS,
      initialSortField: 'dueDate',
      initialSortDirection: 'asc',
    });

  return (
    <ReportLayout
      title="Orders Due During Date Range"
      description="View all orders due within a specified date range to plan production and delivery schedules."
      isLoading={false}
      error={null}
      filters={
        <>
          <ManufacturerFilter
            isAdmin={isAdmin}
            selectedManufacturerId={selectedManufacturerId}
            onManufacturerChange={handleManufacturerChange}
          />
          {isManufacturer && (
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="dealer-filter-label" shrink>
                Dealer
              </InputLabel>
              <Select
                labelId="dealer-filter-label"
                value={selectedDealerId ?? ''}
                label="Dealer"
                displayEmpty
                notched
                onChange={e => {
                  const val = e.target.value;
                  setSelectedDealerId(val === '' ? undefined : Number(val));
                }}
                disabled={!resolvedManufacturerId}
                renderValue={selected => {
                  if (!selected) return 'All Dealers';
                  const dealer = dealers.find(d => d.id === Number(selected));
                  return dealer?.name ?? '';
                }}
              >
                <MenuItem value="">All Dealers</MenuItem>
                {dealers.map(d => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={e => handleStartDateChange(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
            error={dateError?.includes('Start') ?? false}
            helperText={dateError?.includes('Start') ? dateError : undefined}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={e => handleEndDateChange(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
            error={dateError?.includes('End') ?? false}
            helperText={dateError?.includes('End') ? dateError : undefined}
          />
          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel id="quick-select-label">Quick Select</InputLabel>
            <Select
              labelId="quick-select-label"
              value={quickSelect}
              label="Quick Select"
              onChange={e => handleQuickSelectChange(e.target.value as QuickSelect)}
            >
              <MenuItem value="custom">Custom Range</MenuItem>
              <MenuItem value="this-week">This Week</MenuItem>
              <MenuItem value="next-week">Next Week</MenuItem>
              <MenuItem value="this-month">This Month</MenuItem>
              <MenuItem value="next-month">Next Month</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            onClick={handleUpdateReport}
            disabled={isLoading}
            sx={{ height: 40 }}
          >
            Update Report
          </Button>
        </>
      }
    >
      {needsManufacturer ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="text.secondary">
            Please select a Manufacturer to view the report.
          </Typography>
        </Box>
      ) : isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="text.secondary">Loading report...</Typography>
        </Box>
      ) : error ? (
        <Typography color="error">Failed to load report data. Please try again later.</Typography>
      ) : reportData ? (
        <Card>
          <CardContent>
            {titleDateRange && (
              <Typography variant="h6" align="center" gutterBottom>
                Orders Due: {titleDateRange}
              </Typography>
            )}

            <Typography align="center" sx={{ mb: 3, fontSize: '1.1rem' }}>
              <Box
                component="span"
                sx={{ fontWeight: 'bold', color: 'error.main', fontSize: '1.5rem' }}
              >
                {reportData.totalCount}
              </Box>{' '}
              orders due during this period
            </Typography>

            {reportData.orders.length === 0 ? (
              <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                No orders due in the selected date range.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableHeadCell
                        sx={{ fontWeight: 'bold' }}
                        {...getSortProps('orderNumber')}
                      >
                        Order #
                      </SortableHeadCell>
                      {isManufacturer && (
                        <SortableHeadCell
                          sx={{ fontWeight: 'bold' }}
                          {...getSortProps('dealerName')}
                        >
                          Dealer
                        </SortableHeadCell>
                      )}
                      <SortableHeadCell sx={{ fontWeight: 'bold' }} {...getSortProps('customer')}>
                        Customer
                      </SortableHeadCell>
                      <SortableHeadCell sx={{ fontWeight: 'bold' }} {...getSortProps('dueDate')}>
                        Due Date
                      </SortableHeadCell>
                      <SortableHeadCell sx={{ fontWeight: 'bold' }} {...getSortProps('status')}>
                        Status
                      </SortableHeadCell>
                      <SortableHeadCell
                        sx={{ fontWeight: 'bold' }}
                        align="right"
                        {...getSortProps('total')}
                      >
                        Total
                      </SortableHeadCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageRows.map(order => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                            onClick={() => navigate(`/order-details/${order.id}`)}
                          >
                            {order.orderNumber}
                          </Link>
                        </TableCell>
                        {isManufacturer && <TableCell>{order.dealerName ?? '—'}</TableCell>}
                        <TableCell>{order.customer}</TableCell>
                        <TableCell>{formatReportDate(order.dueDate)}</TableCell>
                        <TableCell>
                          {ORDER_STATUS_DISPLAY[order.status] ?? `Status ${order.status}`}
                        </TableCell>
                        <TableCell align="right">{formatReportCurrency(order.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <ReportTablePagination
                  count={reportData.orders.length}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            )}
          </CardContent>
        </Card>
      ) : null}
    </ReportLayout>
  );
}
